import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {LinkingService} from '../../linking/linking.service';
import {VivoService} from '../../vivo/vivo.service';
import {MapElement} from '../../shared/map/map-element.model';
import {GeometryObject} from 'geojson';
import {AppMessageService} from '../../shared/app-message/app-message.service';
import {NotificationService} from '../../components/notification/notification.service';
import {AlarmService} from '../../alarm/alarm.service';
import {AuthService} from '../../auth/auth.service';
import {BaseComponent} from '../../components/general/base/base.component';
import {FilterEvent, TypeFilters} from '../../sidebar/filters/filters.model';
import {_, parseToDate} from '../../shared/utils';
import { BackendAlarm } from '../../alarm/alarm-definitions/backend-alarm';
import { LoadingConfig } from '../../components/qm-directives/loading.directive';

@Component({
  selector: 'qm-element-timeline',
  templateUrl: './element-timeline.component.html',
  styleUrls: ['./element-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ElementTimelineComponent extends BaseComponent implements AfterViewInit, OnInit {

  // this is just stupid way to get translations
  private day: string = _('Day');
  private week: string = _('Week');
  private month: string = _('Month');
  private year: string = _('Year');

  public alarmType: string = 'All';
  public startDate: Date;
  public endDate: Date;
  public dateFilter: string =  'All';
  public typeFilters: TypeFilters = {
    name: 'Type',
    filter_types: [
      'All',
      'Alarms'
    ],
    selected_type: 'All'
  };

  /**
   * Currently selected feature
   */
  @Input()
  public currentFeature: MapElement<GeometryObject>;
  /**
   * List of VIVO events TODO class instead of any!
   */
  public events: any[] = [];
  /**
   * Active notification
   */
  public activeId: string;
  public asset: any;
  public loadingConfig: LoadingConfig = {
    messages: {
      empty: _('Timeline is not available'),
    }
  };
  private taskID: string;
  private vivoId: string;
  private vivoType: 'Asset' | 'Organization';

  /**
   * Paging
   */
  currentPage: number = 1;
  pageSize: number = 10;

  /**
   * Default constructor
   * @param {VivoService} vivoService
   * @param {ChangeDetectorRef} changeDetector
   * @param {NotificationService} notificationService
   * @param {AlarmService} alarmService
   * @param {AppMessageService} appMessageService
   */
  constructor(private changeDetector: ChangeDetectorRef,
              private vivoService: VivoService,
              private readonly linkingService: LinkingService,
              private auth: AuthService,
              private alarmService: AlarmService,
              protected appMessageService: AppMessageService) {
    super();
    this.name = ElementTimelineComponent.getComponentName();
  }

  private static getComponentName(): string {
    return 'ElementTimeline';
  }

  /**
   * Angular lifecycle hook
   */
  ngOnInit() {
    //// add those selections to array, that are not general and available to all tenants
    if (this.auth.hasPermission('vivo')) {
      this.typeFilters.filter_types = [
        ...this.typeFilters.filter_types,
        'VIVO'
      ];
    }
  }

  /**
   * Angular lifecycle hook
   */
  ngAfterViewInit() {
    this.selectFeature();
  }

  private async selectFeature() {
    try {
      this.loadMore();
      if (this.auth.hasPermission('vivo')) {
        this.loading = true;
        const linkings = await this.linkingService.getLinkingsForRegoId(
          this.currentFeature.properties[this.currentFeature.layer.metadata.uniqueBy],
          'vivo',
        );
        const linking = linkings?.[0]; // TODO? multiple linkings support
        if (linking) {
          this.vivoId = linking.sourceId;
          this.vivoType = linking.sourceLinkType as 'Asset'|'Organization';
          this.getAssetDetails();
          if (this.typeFilters.selected_type === 'VIVO' || this.typeFilters.selected_type === 'All') {
            await this.getEvents();
            this.loading = false;
          }
        }
      }
    } catch (e) {
      console.warn(e);
    }
  }

  public filtersChanged($event: FilterEvent) {
    this.startDate = $event.dateFilters.startDate;
    this.endDate = $event.dateFilters.endDate;
    this.dateFilter = $event.dateFilters.selected_type;
    this.typeFilters = $event.typeFilters;
    this.loadMore();
  }

  private async loadMore() {
    this.loading = true;
    this.events = [];
    if (this.typeFilters.selected_type === 'VIVO') {
      await this.getEvents();
    } else if (this.typeFilters.selected_type === 'Alarms') {
      await this.getAlarms();
    } else {
      await Promise.all([this.getAlarms(), this.getEvents()]);
    }
    this.loading = false;
    this.changeDetector.markForCheck();
  }

  /**
   * Return link to VIVO for current map object
   * @returns {string}
   */
  public getVivoLink() {
      if (this.vivoType === 'Organization') {
        return 'https://miprovivo.trackinno.com/organizationNodes/' + this.vivoId + '/assets';
      } else {
        return 'https://miprovivo.trackinno.com/tasks/' + this.taskID;
      }
  }

  getbadgeColor(type: string, status?: string) {
    switch (type) {
      case 'newComment':
        return 'badge-comment';
      default:
        return this.getBadgeClass(status);
    }
  }

  /**
   * Get timeline badge class based on task status
   * @param {string} status
   * @returns {any}
   */
  public getBadgeClass(status: string) {
    switch (status) {
      case 'completed':
        return 'badge-info';
      case 'success':
        return 'badge-success';
      case 'info':
        return 'badge-info';
      case 'danger':
        return 'badge-danger';
      default:
        return 'badge-warning';
    }
  }

  /**
   * isActive return true/false depending which notification mouse is hovering on
   * @param {any} event
   */
  public isActive(event: any) {
    if (event.id === this.activeId) {
      return true;
    }
    return false;
  }

  /**
   * setActive sets activeId which is used to indentify notification from list where mouse is on top of it
   * @param {any} event
   */
  public setActive(event: any) {
    if (event) {
      this.activeId = event.id;
      this.taskID = event.taskId;
    } else {
      this.activeId = null;
    }
  }


  private async getAssetDetails() {
    try {
      const data = await this.vivoService.getAssetInfo(this.vivoId);
      const picture = await this.vivoService.getAssetImage(data.defaultPicture);
      console.log(picture);
      this.changeDetector.markForCheck();
    } catch (error) {
    }
  }

  /**
   * Fetch list of events from VIVO and parse list to more usable format
   */
  private async getEvents() {
    if (!this.vivoId) {
      return;
    }
    let values;
    if (this.dateFilter !== 'All') {
      values = await this.vivoService.getEventsByDateWithChildren(this.vivoId, this.startDate, this.endDate);
    } else {
      values = await this.vivoService.getEventsWithChildren(this.vivoId, this.vivoType, 50);
    }
    console.log('Result count', values);
    // console.log('VALUES', values);
    if (values.docs) {
      for (const event of values.docs) {
        if (event.task && event.user) {
          // console.log('VALUES EVENT', event);
          const entry: any = {};
          entry.id = 'vivo_' + event._id;
          entry.taskid = event.task._id;
          entry.text = event.task.name;
          if (event.task.message) {
            entry.text = entry.text + ', ' + event.task.message;
          }
          entry.status = event.task.status;
          entry.timestamp = parseToDate(event.task.updatedAt);
          entry.user = event.user.username;
          entry.taskType = event.type;
          if (event.task && event.task.parents[0].asset.holder) {
            entry.assetName = event.task.parents[0].asset.holder.name;
          }
          this.events.push(entry);
        } else if (event.type === 'newComment') {
          const entry: any = {};
          entry.id = 'vivo_' + event._id;
          entry.text = event.contentData;
          entry.status = 'success';
          entry.timestamp = parseToDate(event.createdAt);
          entry.taskType = event.type;
          if (event.task) {
            entry.assetName = event.task.parents[0].asset.holder.name;
          }
          try {
            entry.user = event.user.username;
          } catch (e) {
            entry.user = 'Unknown user';
          }
          this.events.push(entry);
        } else {
          console.log('Unknown event type', event.type);
          console.log(event);
        }
      }
    }
    this.changeDetector.markForCheck();
  }

  private async getAlarms() {
    let query;
    const linkings = await this.linkingService.getLinkingsForRegoId(
      this.currentFeature.properties[this.currentFeature.layer.metadata.uniqueBy],
      'misonet',
    );
    const linking = linkings?.[0]; // TODO? multiple linkings support
    if (linking) {
      if (this.dateFilter !== 'All') {
        query = 'payload.p_deviceid=' + linking.sourceId + '&createdAt<' + this.endDate.toISOString() +
          '&createdAt>' + this.startDate.toISOString() + '&sort=-createdAt&limit=900';
      } else {
        query = 'payload.p_deviceid=' + linking.sourceId + '&sort=-createdAt&limit=900';
      }
    } else {
      // TODO Else block works only for trains!
      if (this.dateFilter !== 'All') {
        query = 'payload.tripNumber=' + this.currentFeature.properties.tripNumber + '&createdAt<' +
          this.endDate.toJSON() + '&createdAt>' + this.startDate.toJSON() + '&sort=-activatedAt&limit=900';
      } else {
        query = 'payload.tripNumber="' + this.currentFeature.properties.tripNumber + '"&sort=-activatedAt&limit=900';
      }
    }
    const alarms = await this.alarmService.queryAlarms(query);
    if (!alarms.isOk) {
      this.loading = alarms;
    } else {
      this.allAlarms(alarms.data.data);
    }
  }

  private allAlarms(a: BackendAlarm[]) {
    if (a) {
      for (const row of a) {
        if (!row.activatedAt) {
          continue;
        }
        const entry: any = {};
        entry.id = 'alarm_' + row._id;
        entry.text = this.alarmService.getMessage(row, true);
        entry.translate = this.alarmService.setTranslateProperties(row);
        entry.timestamp = parseToDate(row.activatedAt);
        entry.user = 'Mipro REGO';
        entry.status = row.active ? 'danger' : 'success';
        this.events.push(entry);
      }
      console.log(a);
    }
    this.changeDetector.markForCheck();
  }
}
